import { SUBSCRIPTION_PLANS } from '../../config/subscriptionPlans';
import { subscriptionCycleLabel } from '../../utils/subscriptionCycle';

export const PREVIEW_TIERS = ['free', 'standard', 'professional', 'premium'] as const;
export type PreviewTier = typeof PREVIEW_TIERS[number];
// Provisional API budgets, used only by the local Admin simulation.
export const PREVIEW_API_USD = { free: 0.30, standard: 1.20, professional: 6, premium: 12 } as const;
export const PREVIEW_CAPACITY = { free: 'Para probar Juris', standard: 'Para uso ocasional',
  professional: 'Para uso habitual', premium: 'Para uso intensivo' } as const;
type Source = 'plan' | 'credits';
export const DOCUMENT_STAGES = ['Herramientas', 'Proceso asíncrono y QA', 'Entrega'] as const;
export interface PreviewState {
  tier: PreviewTier;
  remaining: number;
  creditCents: number;
  creditsActive: boolean;
  creditsNotice: boolean;
  creditActivation: number;
  clock: number;
  renewAt: number;
  cycle: number;
  pendingTier: PreviewTier | null;
  cancelAtPeriodEnd: boolean;
  work: { stage: number; source: Source; cycle: number; stepUnits: number } | null;
  announcement: string;
}
export type PreviewAction =
  | { type: 'percent'; value: number } | { type: 'balance'; cents: number }
  | { type: 'topup'; cents: 500 | 1000 | 2000 } | { type: 'activate' }
  | { type: 'plan'; tier: PreviewTier } | { type: 'viewPlan'; tier: PreviewTier }
  | { type: 'renew' } | { type: 'cancelChange' } | { type: 'cancelSubscription' }
  | { type: 'consult' } | { type: 'document' } | { type: 'advance' }
  | { type: 'messageSent'; cycle: number; activation: number }
  | { type: 'scenario'; value: 'normal' | 'low' | 'credits' | 'empty' | 'active' | 'document' };
export const budget = (tier: PreviewTier) => Math.round(PREVIEW_API_USD[tier] * 1_000_000);
export const remainingPercent = (state: PreviewState) => Math.min(100, Math.ceil(state.remaining / budget(state.tier) * 100));
export const previewPlanName = (tier: PreviewTier) => SUBSCRIPTION_PLANS[tier].name;
export const euros = (cents: number) => (cents / 100).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' });
export const renewalLabel = (ms: number) => new Date(ms).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
export const previewCycleLabel = (state: PreviewState) => subscriptionCycleLabel(state.cancelAtPeriodEnd, Boolean(state.pendingTier));
export const scheduledPlanName = (state: PreviewState) => state.cancelAtPeriodEnd ? 'Prueba'
  : state.pendingTier ? previewPlanName(state.pendingTier).replace(/^Plan /u, '') : null;
export const capacityTone = (percent: number) => percent <= 10 ? { bar: 'bg-danger', text: 'text-danger' }
  : percent <= 25 ? { bar: 'bg-warning', text: 'text-warning' } : { bar: 'bg-juris', text: 'text-ink' };
export const previewSendingDisabled = (state: PreviewState | null | undefined) => Boolean(state && !admittedSource(state));

export function nextMonth(ms: number) {
  const date = new Date(ms), day = date.getUTCDate();
  date.setUTCDate(1); date.setUTCMonth(date.getUTCMonth() + 1);
  const last = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0)).getUTCDate();
  date.setUTCDate(Math.min(day, last));
  return date.getTime();
}
export function initialPreview(now: number): PreviewState {
  return { tier: 'professional', remaining: budget('professional') * 0.82, creditCents: 1000,
    creditsActive: false, creditsNotice: false, creditActivation: 0,
    clock: now, renewAt: nextMonth(now), cycle: 1, pendingTier: null, cancelAtPeriodEnd: false,
    work: null, announcement: '' };
}
export function admittedSource(state: PreviewState): Source | null {
  if (state.remaining > 0) return 'plan';
  return state.creditsActive && state.creditCents > 0 ? 'credits' : null;
}
function spend(state: PreviewState, source: Source, stepUnits: number, cycle: number): PreviewState {
  // Independent visual fixtures, NOT a USD/EUR exchange rate or commercial price.
  if (cycle !== state.cycle) return state;
  if (source === 'credits') return { ...state, creditCents: Math.max(0, state.creditCents - 25), creditsNotice: false };
  return cycle === state.cycle ? { ...state, remaining: Math.max(0, state.remaining - stepUnits) } : state;
}
function newCycle(state: PreviewState, tier: PreviewTier, clock: number): PreviewState {
  return { ...state, tier, clock, remaining: budget(tier), renewAt: nextMonth(clock),
    cycle: state.cycle + 1, pendingTier: null, cancelAtPeriodEnd: false, creditsActive: false, creditsNotice: false };
}
export function reducePreview(state: PreviewState, action: PreviewAction): PreviewState {
  switch (action.type) {
    case 'percent': {
      if (!Number.isFinite(action.value)) return state;
      const remaining = Math.round(budget(state.tier) * Math.max(0, Math.min(100, action.value)) / 100);
      return { ...state, remaining, creditsActive: remaining > 0 ? false : state.creditsActive,
        creditsNotice: remaining > 0 ? false : state.creditsNotice, announcement: '' };
    }
    case 'balance':
      return Number.isFinite(action.cents) ? { ...state, creditCents: Math.max(0, Math.min(100_000, Math.round(action.cents))) } : state;
    case 'topup':
      return { ...state, creditCents: state.creditCents + action.cents, announcement: `Recarga simulada de ${euros(action.cents)}. No se ha realizado ningún pago.` };
    case 'activate':
      return state.remaining === 0 && state.creditCents > 0 && !state.creditsActive
        ? { ...state, creditsActive: true, creditsNotice: true, creditActivation: state.creditActivation + 1, announcement: '' } : state;
    case 'viewPlan':
      return { ...newCycle(state, action.tier, state.clock), work: null, announcement: '' };
    case 'plan':
      if (action.tier === state.tier) return state;
      if (action.tier === 'free') return reducePreview(state, { type: 'cancelSubscription' });
      return PREVIEW_TIERS.indexOf(action.tier) > PREVIEW_TIERS.indexOf(state.tier)
        ? { ...newCycle(state, action.tier, state.clock), announcement: 'Subida simulada: nueva cuota y nuevo ciclo. Sin cobro real.' }
        : { ...state, pendingTier: action.tier, cancelAtPeriodEnd: false, announcement: '' };
    case 'cancelSubscription':
      return state.tier === 'free' ? state : { ...state, pendingTier: null, cancelAtPeriodEnd: true, announcement: '' };
    case 'cancelChange': return { ...state, pendingTier: null, cancelAtPeriodEnd: false, announcement: '' };
    case 'renew':
      return { ...newCycle(state, state.cancelAtPeriodEnd ? 'free' : state.pendingTier ?? state.tier, state.renewAt), announcement: '' };
    case 'messageSent':
      return action.cycle === state.cycle && action.activation === state.creditActivation && state.creditsActive
        ? { ...state, creditsNotice: false } : state;
    case 'consult': {
      const source = admittedSource(state);
      return source ? { ...spend(state, source, budget(state.tier) / 20, state.cycle), announcement: 'Consulta ficticia completada. No se ha enviado ningún mensaje.' } : state;
    }
    case 'document': {
      if (state.work) return state;
      const source = admittedSource(state);
      if (!source) return state;
      const work = { stage: 0, source, cycle: state.cycle, stepUnits: budget(state.tier) / 20 };
      return { ...spend(state, source, work.stepUnits, work.cycle), work, announcement: '' };
    }
    case 'advance': {
      if (!state.work) return state;
      const work = state.work;
      const next = spend(state, work.source, work.stepUnits, work.cycle);
      return work.stage === 2 ? { ...next, work: null, announcement: 'Documento entregado en la simulación. No se ha creado ningún archivo.' }
        : { ...next, work: { ...work, stage: work.stage + 1 }, announcement: '' };
    }
    case 'scenario': {
      const base = { ...state, creditsActive: false, creditsNotice: false, work: null, announcement: '' };
      if (action.value === 'normal') return { ...base, remaining: budget(state.tier) * 0.82, creditCents: 1000 };
      if (action.value === 'low') return { ...base, remaining: budget(state.tier) * 0.01, creditCents: 1000 };
      if (action.value === 'empty') return { ...base, remaining: 0, creditCents: 0 };
      if (action.value === 'document') return { ...base, remaining: 0, creditCents: 0,
        work: { stage: 0, source: 'plan', cycle: state.cycle, stepUnits: budget(state.tier) / 20 } };
      return { ...base, remaining: 0, creditCents: 1000, creditsActive: action.value === 'active',
        creditsNotice: action.value === 'active', creditActivation: state.creditActivation + 1 };
    }
  }
}
